/**
 * docxGenerator — Generation de documents Word (.docx) cote client
 *
 * Utilise la lib `docx` pour creer un document Word a partir des donnees
 * d'un theme natal (positions planetaires, signature, aspects, configurations).
 */

import {
  Document,
  Paragraph,
  TextRun,
  Table,
  TableRow,
  TableCell,
  WidthType,
  AlignmentType,
  HeadingLevel,
  BorderStyle,
  Packer,
  ShadingType,
  PageBreak,
} from 'docx';
import { saveAs } from 'file-saver';
import type { ChartAnalysis, ClientDetail, ChartSummary } from '../types/clients.js';

// ─── Types locaux ────────────────────────────────────────

interface PlanetPosition {
  nom: string;
  degre: string;
  signe: string;
  maison: number;
  retrograde: boolean;
  longitude: number;
}

interface HouseCusp {
  numero: number;
  degre: string;
  signe: string;
  longitude: number;
}

interface ChartData {
  client: {
    prenom: string;
    nom: string;
    dateNaissance: string;
    heureNaissance: string;
    lieuNaissance: string;
  };
  planetes: PlanetPosition[];
  maisons: HouseCusp[];
  source: string;
}

// ─── Constantes de style ─────────────────────────────────

const COLORS = {
  gold: 'C9A227',
  goldLight: 'F5E6B3',
  dark: '1A1A1A',
  mid: '666666',
  light: '999999',
  white: 'FFFFFF',
  bgLight: 'FAFAFA',
  feu: 'E74C3C',
  terre: '27AE60',
  air: '3498DB',
  eau: '8E44AD',
};

const ELEMENT_COLORS: Record<string, string> = {
  Feu: COLORS.feu,
  Terre: COLORS.terre,
  Air: COLORS.air,
  Eau: COLORS.eau,
};

// ─── Helpers ─────────────────────────────────────────────

function noBorder() {
  return {
    top: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
    bottom: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
    left: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
    right: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
  };
}

function lightBorder() {
  return {
    top: { style: BorderStyle.SINGLE, size: 1, color: 'E5E5E5' },
    bottom: { style: BorderStyle.SINGLE, size: 1, color: 'E5E5E5' },
    left: { style: BorderStyle.SINGLE, size: 1, color: 'E5E5E5' },
    right: { style: BorderStyle.SINGLE, size: 1, color: 'E5E5E5' },
  };
}

function headerCell(text: string, width?: number): TableCell {
  return new TableCell({
    width: width ? { size: width, type: WidthType.PERCENTAGE } : undefined,
    shading: { type: ShadingType.SOLID, color: COLORS.gold },
    borders: noBorder(),
    children: [
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { before: 40, after: 40 },
        children: [new TextRun({ text, bold: true, size: 18, color: COLORS.white, font: 'Calibri' })],
      }),
    ],
  });
}

function dataCell(text: string, bold = false): TableCell {
  return new TableCell({
    borders: lightBorder(),
    children: [
      new Paragraph({
        spacing: { before: 30, after: 30 },
        children: [new TextRun({ text, size: 18, color: bold ? COLORS.dark : COLORS.mid, bold, font: 'Calibri' })],
      }),
    ],
  });
}

function sectionTitle(text: string): Paragraph {
  return new Paragraph({
    heading: HeadingLevel.HEADING_2,
    spacing: { before: 400, after: 200 },
    children: [
      new TextRun({ text, bold: true, size: 26, color: COLORS.gold, font: 'Calibri' }),
    ],
  });
}

function subTitle(text: string): Paragraph {
  return new Paragraph({
    heading: HeadingLevel.HEADING_3,
    spacing: { before: 250, after: 120 },
    children: [
      new TextRun({ text, bold: true, size: 22, color: COLORS.dark, font: 'Calibri' }),
    ],
  });
}

function bodyText(text: string): Paragraph {
  return new Paragraph({
    spacing: { before: 60, after: 60 },
    children: [new TextRun({ text, size: 20, color: COLORS.mid, font: 'Calibri' })],
  });
}

function spacer(): Paragraph {
  return new Paragraph({ spacing: { before: 100, after: 100 }, children: [] });
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
}

// ─── Sections du document ────────────────────────────────

function buildCoverPage(client: ClientDetail, chartData: ChartData): Paragraph[] {
  return [
    new Paragraph({ spacing: { before: 2000 }, children: [] }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 200 },
      children: [new TextRun({ text: 'THEME NATAL', size: 44, bold: true, color: COLORS.gold, font: 'Calibri' })],
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 100 },
      children: [
        new TextRun({
          text: `${client.prenom} ${client.nom}`,
          size: 36,
          bold: true,
          color: COLORS.dark,
          font: 'Calibri',
        }),
      ],
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 60 },
      children: [
        new TextRun({
          text: `Ne(e) le ${formatDate(client.dateNaissance)}`,
          size: 22,
          color: COLORS.mid,
          font: 'Calibri',
        }),
      ],
    }),
    ...(chartData.client.heureNaissance
      ? [
          new Paragraph({
            alignment: AlignmentType.CENTER,
            spacing: { after: 60 },
            children: [
              new TextRun({
                text: `a ${chartData.client.heureNaissance}`,
                size: 22,
                color: COLORS.mid,
                font: 'Calibri',
              }),
            ],
          }),
        ]
      : []),
    ...(client.lieuNaissance
      ? [
          new Paragraph({
            alignment: AlignmentType.CENTER,
            spacing: { after: 300 },
            children: [
              new TextRun({
                text: `a ${client.lieuNaissance}${client.paysNaissance ? ` (${client.paysNaissance})` : ''}`,
                size: 22,
                color: COLORS.mid,
                font: 'Calibri',
              }),
            ],
          }),
        ]
      : []),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { before: 600 },
      children: [
        new TextRun({
          text: `Document genere le ${new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}`,
          size: 18,
          color: COLORS.light,
          font: 'Calibri',
          italics: true,
        }),
      ],
    }),
    new Paragraph({ children: [new PageBreak()] }),
  ];
}

function buildPositionsTable(planetes: PlanetPosition[]): (Paragraph | Table)[] {
  const rows = [
    new TableRow({
      children: [
        headerCell('Planete', 20),
        headerCell('Signe', 20),
        headerCell('Degre', 20),
        headerCell('Maison', 15),
        headerCell('Retro', 10),
      ],
    }),
    ...planetes.map(
      (p) =>
        new TableRow({
          children: [
            dataCell(p.nom, true),
            dataCell(p.signe),
            dataCell(p.degre),
            dataCell(`Maison ${p.maison}`),
            dataCell(p.retrograde ? 'R' : ''),
          ],
        }),
    ),
  ];

  return [
    sectionTitle('Positions Planetaires'),
    new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      rows,
    }),
  ];
}

function buildHousesTable(maisons: HouseCusp[]): (Paragraph | Table)[] {
  const rows = [
    new TableRow({
      children: [
        headerCell('Maison', 25),
        headerCell('Signe', 30),
        headerCell('Degre', 45),
      ],
    }),
    ...maisons.map(
      (m) =>
        new TableRow({
          children: [
            dataCell(`Maison ${m.numero}`, true),
            dataCell(m.signe),
            dataCell(m.degre),
          ],
        }),
    ),
  ];

  return [
    sectionTitle('Cuspides des Maisons'),
    new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      rows,
    }),
  ];
}

function buildSignatureSection(analysis: ChartAnalysis): (Paragraph | Table)[] {
  const { signature } = analysis;
  const elements: (Paragraph | Table)[] = [
    sectionTitle('Signature Energetique'),
    bodyText(
      `Dominante : ${signature.dominante.element} ${signature.dominante.mode}`,
    ),
    spacer(),
    subTitle('Repartition par Element'),
  ];

  // Table elements
  const elementRows = [
    new TableRow({
      children: [
        headerCell('Element', 25),
        headerCell('Poids', 15),
        headerCell('%', 15),
        headerCell('Planetes', 45),
      ],
    }),
    ...Object.entries(signature.elements).map(
      ([element, data]) =>
        new TableRow({
          children: [
            new TableCell({
              borders: lightBorder(),
              shading: { type: ShadingType.SOLID, color: COLORS.bgLight },
              children: [
                new Paragraph({
                  spacing: { before: 30, after: 30 },
                  children: [new TextRun({ text: element, bold: true, size: 18, color: ELEMENT_COLORS[element] ?? COLORS.dark, font: 'Calibri' })],
                }),
              ],
            }),
            dataCell(String(data.weight)),
            dataCell(`${data.percentage}%`),
            dataCell(data.planetes.join(', ')),
          ],
        }),
    ),
  ];

  elements.push(
    new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, rows: elementRows }),
    spacer(),
    subTitle('Repartition par Mode'),
  );

  // Table modes
  const modeRows = [
    new TableRow({
      children: [
        headerCell('Mode', 25),
        headerCell('Poids', 15),
        headerCell('%', 15),
        headerCell('Planetes', 45),
      ],
    }),
    ...Object.entries(signature.modes).map(
      ([mode, data]) =>
        new TableRow({
          children: [
            dataCell(mode, true),
            dataCell(String(data.weight)),
            dataCell(`${data.percentage}%`),
            dataCell(data.planetes.join(', ')),
          ],
        }),
    ),
  ];

  elements.push(
    new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, rows: modeRows }),
    spacer(),
    subTitle('Polarites'),
  );

  // Table polarites
  const polRows = [
    new TableRow({
      children: [headerCell('Polarite', 30), headerCell('Nombre', 30), headerCell('%', 40)],
    }),
    ...Object.entries(signature.polarites).map(
      ([pol, data]) =>
        new TableRow({
          children: [
            dataCell(pol, true),
            dataCell(String(data.count)),
            dataCell(`${data.percentage}%`),
          ],
        }),
    ),
  ];

  elements.push(
    new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, rows: polRows }),
  );

  return elements;
}

function buildAspectsSection(analysis: ChartAnalysis): (Paragraph | Table)[] {
  const { aspects } = analysis;
  const elements: (Paragraph | Table)[] = [
    new Paragraph({ children: [new PageBreak()] }),
    sectionTitle('Aspects Principaux'),
  ];

  // Statistiques
  if (aspects.statistiques) {
    const statsText = Object.entries(aspects.statistiques)
      .map(([type, count]) => `${type} : ${count}`)
      .join(' | ');
    elements.push(bodyText(statsText), spacer());
  }

  // Table des aspects
  if (aspects.aspects.length > 0) {
    const rows = [
      new TableRow({
        children: [
          headerCell('Planete 1', 20),
          headerCell('Aspect', 20),
          headerCell('Planete 2', 20),
          headerCell('Orbe', 15),
          headerCell('Nature', 25),
        ],
      }),
      ...aspects.aspects.map(
        (a) =>
          new TableRow({
            children: [
              dataCell(a.planete1, true),
              dataCell(a.type),
              dataCell(a.planete2, true),
              dataCell(`${a.orbe.toFixed(1)}°`),
              dataCell(a.nature),
            ],
          }),
      ),
    ];

    elements.push(
      new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, rows }),
    );
  }

  return elements;
}

function buildConfigurationsSection(analysis: ChartAnalysis): Paragraph[] {
  if (!analysis.configurations || analysis.configurations.length === 0) {
    return [
      sectionTitle('Configurations'),
      bodyText('Aucune configuration majeure detectee.'),
    ];
  }

  const elements: Paragraph[] = [
    sectionTitle('Configurations'),
  ];

  for (const config of analysis.configurations) {
    const planetes = config.planetes?.join(', ') ?? '';
    elements.push(
      subTitle(config.type),
      bodyText(`Planetes impliquees : ${planetes}`),
    );
    if (config.apex) {
      elements.push(bodyText(`Apex : ${config.apex}`));
    }
    if (config.base && config.base.length > 0) {
      elements.push(bodyText(`Base : ${config.base.join(', ')}`));
    }
    if (config.element) {
      elements.push(bodyText(`Element : ${config.element}`));
    }
    elements.push(spacer());
  }

  return elements;
}

// Descriptions par signe pour l'axe nodal
const DESCRIPTIONS_NORD: Record<string, string> = {
  'Belier': "Developper l'affirmation de soi, le courage, l'initiative personnelle",
  'Taureau': "Cultiver la stabilite, les valeurs propres, le rapport a la matiere",
  'Gemeaux': "Apprendre la communication, la curiosite, les echanges multiples",
  'Cancer': "Developper la sensibilite, l'intimite, les racines emotionnelles",
  'Lion': "Exprimer sa creativite, son unicite, prendre sa place au centre",
  'Vierge': "Cultiver le discernement, le service, l'amelioration concrete",
  'Balance': "Apprendre la relation, le compromis, l'harmonie avec l'autre",
  'Scorpion': "Explorer les profondeurs, la transformation, le pouvoir partage",
  'Sagittaire': "Developper la vision, la quete de sens, l'expansion spirituelle",
  'Capricorne': "Construire avec responsabilite, ambition, structuration",
  'Verseau': "Cultiver l'originalite, le collectif, la liberte d'etre different",
  'Poissons': "Developper la compassion, la spiritualite, le lacher-prise",
};

const DESCRIPTIONS_SUD: Record<string, string> = {
  'Belier': "Impulsivite passee, egocentrisme a depasser",
  'Taureau': "Attachement excessif a la securite materielle",
  'Gemeaux': "Dispersion passee, superficialite dans les echanges",
  'Cancer': "Dependance emotionnelle, refuge excessif dans le passe",
  'Lion': "Ego surdimensionne, besoin de reconnaissance a transformer",
  'Vierge': "Perfectionnisme paralysant, critique excessive",
  'Balance': "Tendance passee a trop chercher l'approbation des autres",
  'Scorpion': "Attachements anciens au controle et aux possessions partagees",
  'Sagittaire': "Exces de certitudes, tendance au dogmatisme",
  'Capricorne': "Rigidite passee, trop de focus sur le statut social",
  'Verseau': "Detachement excessif, fuite dans le collectif",
  'Poissons': "Tendance a la fuite, confusion des limites personnelles",
};

/** Normalise un nom de signe pour la recherche dans les descriptions */
function normalizeSigne(signe: string): string {
  return signe.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

function buildNodalSection(planetes: PlanetPosition[]): Paragraph[] {
  const noeudNord = planetes.find((p) =>
    p.nom === 'Noeud Nord' || p.nom === 'Noeud Lunaire Nord' || p.nom === 'Noeud N.',
  );
  const noeudSud = planetes.find((p) =>
    p.nom === 'Noeud Sud' || p.nom === 'Noeud Lunaire Sud' || p.nom === 'Noeud S.',
  );

  if (!noeudNord && !noeudSud) {
    return [];
  }

  const elements: Paragraph[] = [sectionTitle('Axe Nodal')];

  if (noeudNord) {
    const signeNorm = normalizeSigne(noeudNord.signe);
    const desc = DESCRIPTIONS_NORD[signeNorm] ?? '';
    elements.push(
      subTitle('Noeud Nord — Direction de vie'),
      bodyText(`${noeudNord.signe} — ${noeudNord.degre} — Maison ${noeudNord.maison}`),
    );
    if (desc) elements.push(bodyText(`Direction d'evolution : ${desc}`));
  }

  if (noeudSud) {
    const signeNorm = normalizeSigne(noeudSud.signe);
    const desc = DESCRIPTIONS_SUD[signeNorm] ?? '';
    elements.push(
      spacer(),
      subTitle('Noeud Sud — Acquis karmiques'),
      bodyText(`${noeudSud.signe} — ${noeudSud.degre} — Maison ${noeudSud.maison}`),
    );
    if (desc) elements.push(bodyText(`Bagages karmiques : ${desc}`));
  }

  return elements;
}

function buildStructureSection(analysis: ChartAnalysis): (Paragraph | Table)[] {
  if (!analysis.structureSpatiale) return [];

  const ss = analysis.structureSpatiale;
  const elements: (Paragraph | Table)[] = [
    new Paragraph({ children: [new PageBreak()] }),
    sectionTitle('Structure Spatiale'),
    subTitle('Hemispheres'),
  ];

  // Table hemispheres
  const hemiRows = [
    new TableRow({ children: [headerCell('Direction', 25), headerCell('Planetes', 30), headerCell('%', 15), headerCell('Nombre', 15)] }),
    ...(['Est', 'Ouest', 'Nord', 'Sud'] as const).map(
      (dir) => new TableRow({
        children: [
          dataCell(dir, true),
          dataCell(ss.hemispheres[dir].planetes.join(' ')),
          dataCell(`${ss.hemispheres[dir].percentage}%`),
          dataCell(String(ss.hemispheres[dir].count)),
        ],
      }),
    ),
  ];
  elements.push(new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, rows: hemiRows }));
  elements.push(spacer(), bodyText(`Orientation dominante : ${ss.orientationDominante}`));

  // Table quadrants
  elements.push(spacer(), subTitle('Quadrants'));
  const quadRows = [
    new TableRow({ children: [headerCell('Quadrant', 15), headerCell('Signification', 35), headerCell('Nb', 10), headerCell('Planetes', 40)] }),
    ...Object.entries(ss.quadrants).map(
      ([key, q]) => new TableRow({
        children: [
          dataCell(key, true),
          dataCell(q.signification),
          dataCell(String(q.count)),
          dataCell(q.planetes.join(' ')),
        ],
      }),
    ),
  ];
  elements.push(new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, rows: quadRows }));

  return elements;
}

function buildDignitesSection(analysis: ChartAnalysis): (Paragraph | Table)[] {
  if (!analysis.dignites) return [];

  const dig = analysis.dignites;
  const elements: (Paragraph | Table)[] = [
    sectionTitle('Dignites Planetaires'),
  ];

  const allResults = [
    ...dig.forces.map((d) => ({ ...d, categorie: 'Force' })),
    ...dig.difficultes.map((d) => ({ ...d, categorie: 'Difficulte' })),
    ...dig.neutres.map((d) => ({ ...d, categorie: 'Peregrine' })),
  ];

  if (allResults.length > 0) {
    const rows = [
      new TableRow({ children: [headerCell('Planete', 20), headerCell('Signe', 18), headerCell('Maison', 12), headerCell('Dignite', 20), headerCell('Categorie', 20)] }),
      ...allResults.map(
        (d) => new TableRow({
          children: [
            dataCell(d.planete, true),
            dataCell(d.signe),
            dataCell(`M${d.maison}`),
            dataCell(d.label),
            dataCell(d.categorie),
          ],
        }),
      ),
    ];
    elements.push(new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, rows }));
  }

  // Synthese
  const synLabels: Record<string, string> = {
    forces_dominantes: 'Forces dominantes — plusieurs planetes en dignite',
    defis_marques: 'Defis marques — plusieurs planetes en difficulte',
    equilibre: 'Equilibre — repartition harmonieuse des dignites',
  };
  elements.push(spacer(), bodyText(`Synthese : ${synLabels[dig.synthese] ?? dig.synthese}`));

  return elements;
}

// ─── Fonction principale ─────────────────────────────────

/**
 * Genere un document Word (.docx) pour un theme natal et le telecharge.
 */
export async function generateNatalDocx(
  client: ClientDetail,
  _chart: ChartSummary,
  chartData: ChartData,
  analysis: ChartAnalysis,
): Promise<void> {
  const doc = new Document({
    creator: 'Arketyp',
    title: `Theme Natal — ${client.prenom} ${client.nom}`,
    description: 'Document genere par Arketyp — Astrologie Humaniste Evolutive',
    styles: {
      default: {
        document: {
          run: { font: 'Calibri', size: 20, color: COLORS.dark },
        },
      },
    },
    sections: [
      {
        properties: {
          page: {
            margin: { top: 1440, bottom: 1440, left: 1440, right: 1440 },
          },
        },
        children: [
          ...buildCoverPage(client, chartData),
          ...buildPositionsTable(chartData.planetes),
          spacer(),
          ...buildHousesTable(chartData.maisons),
          new Paragraph({ children: [new PageBreak()] }),
          ...buildSignatureSection(analysis),
          ...buildAspectsSection(analysis),
          new Paragraph({ children: [new PageBreak()] }),
          ...buildConfigurationsSection(analysis),
          ...buildStructureSection(analysis),
          ...buildDignitesSection(analysis),
          ...buildNodalSection(chartData.planetes),
          // Pied de page
          spacer(),
          spacer(),
          new Paragraph({
            alignment: AlignmentType.CENTER,
            children: [
              new TextRun({
                text: 'Document genere par Arketyp — Astrologie Humaniste Evolutive',
                size: 16,
                color: COLORS.light,
                italics: true,
                font: 'Calibri',
              }),
            ],
          }),
        ],
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  const filename = `Theme-Natal_${client.prenom}-${client.nom}_${new Date().toISOString().slice(0, 10)}.docx`;
  saveAs(blob, filename);
}
